/**
 * EventAdminService
 * Events hub admin surface on EventManagement: lookups, the admin list (paged), detail, detach,
 * cancel, restore and the review queue.
 * Route: events/api/events (Admin role OR the Event.* right named per method)
 */
import { BaseService } from '../base/BaseService';
import { unwrapEventsResponse as unwrap } from './unwrapEventsResponse';
export class EventAdminService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EventAdminService.DEFAULT_SERVICE_PATH,
            basePath: EventAdminService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- lookups ----
    /** GET api/events/lookups (any signed-in user). */
    async getLookups() {
        return unwrap(await this.getLookupsRaw(), 'Event lookups not found');
    }
    getLookupsRaw() {
        return this.http.get(this.buildUrl('lookups'));
    }
    // ---- admin list + detail ----
    /** GET api/events/admin (Event.View). Paged with skip / take; totalCount comes from the envelope. */
    async listAdmin(query = {}) {
        const res = await this.listAdminRaw(query);
        if (res.data === undefined || res.data === null)
            throw new Error('Events not found');
        const items = res.data;
        return {
            items,
            totalCount: res.totalCount ?? items.length,
            skip: res.skip ?? query.skip ?? 0,
            take: res.take ?? query.take ?? items.length,
        };
    }
    listAdminRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('admin', {
            from: query.from,
            to: query.to,
            sourceId: query.sourceId,
            reviewStatusId: query.reviewStatusId,
            statusId: query.statusId,
            includeDeleted: query.includeDeleted,
            search: query.search?.trim() || undefined,
            skip: query.skip,
            take: query.take,
        }));
    }
    /** GET api/events/admin/{id} (Event.View). */
    async getAdminDetail(id) {
        return unwrap(await this.getAdminDetailRaw(id), `Event ${id} not found`);
    }
    getAdminDetailRaw(id) {
        return this.http.get(this.buildUrl(`admin/${id}`));
    }
    // ---- lifecycle ----
    /** POST api/events/{id}/detach (Event.Update). The source stops changing this event. */
    async detach(id) {
        return unwrap(await this.detachRaw(id), `Could not detach event ${id}`);
    }
    detachRaw(id) {
        return this.http.post(this.buildUrl(`${id}/detach`));
    }
    /** POST api/events/{id}/cancel (Event.Update). */
    async cancel(id, request = {}) {
        return unwrap(await this.cancelRaw(id, request), `Could not cancel event ${id}`);
    }
    cancelRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`${id}/cancel`), request);
    }
    /** POST api/events/{id}/restore (Event.Update). Brings back a cancelled or deleted event. */
    async restore(id) {
        return unwrap(await this.restoreRaw(id), `Could not restore event ${id}`);
    }
    restoreRaw(id) {
        return this.http.post(this.buildUrl(`${id}/restore`));
    }
    // ---- review queue ----
    /** GET api/events/review (Event.Review). Synced events held because a location or tag is unmapped. */
    async getReviewQueue() {
        const res = await this.getReviewQueueRaw();
        return res.data ?? [];
    }
    getReviewQueueRaw() {
        return this.http.get(this.buildUrl('review'));
    }
    /** POST api/events/review/{id}/resolve (Event.Review). */
    async resolveReview(id, request) {
        return unwrap(await this.resolveReviewRaw(id, request), `Could not resolve event ${id}`);
    }
    resolveReviewRaw(id, request) {
        return this.http.post(this.buildUrl(`review/${id}/resolve`), request);
    }
}
EventAdminService.DEFAULT_SERVICE_PATH = 'events';
EventAdminService.BASE_PATH = 'api/events';
//# sourceMappingURL=EventAdminService.js.map